// 所有业务日期都按 Asia/Shanghai 计算。中国没有夏令时，固定 UTC+8。
// 日期统一用 'YYYY-MM-DD' 字符串，时间用 'HH:mm'。

const OFFSET_MS = 8 * 3600_000;
const DAY_MS = 86_400_000;

export const WEEKDAY_CN = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'] as const;

export interface WorkCalendar {
  holidays: string[];
  extraWorkdays: string[];
}

/** 某一时刻在北京时间的日期 */
export function cnDate(d: Date): string {
  return new Date(d.getTime() + OFFSET_MS).toISOString().slice(0, 10);
}

/** 某一时刻在北京时间的 HH:mm */
export function cnTime(d: Date): string {
  return new Date(d.getTime() + OFFSET_MS).toISOString().slice(11, 16);
}

/** 北京时间某天某时对应的时刻 */
export function cnInstant(date: string, time = '00:00'): Date {
  return new Date(`${date}T${time}:00+08:00`);
}

export function addDays(date: string, n: number): string {
  const t = new Date(`${date}T00:00:00Z`).getTime() + n * DAY_MS;
  return new Date(t).toISOString().slice(0, 10);
}

/** b - a 的自然日天数 */
export function diffDays(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS);
}

/** 0 = 周日 … 6 = 周六 */
export function weekdayOf(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay();
}

/** 所在周的周一 */
export function weekStartOf(date: string): string {
  return addDays(date, -((weekdayOf(date) + 6) % 7));
}

/** ISO 周数（周一开始，含 1 月 4 日的那周是第 1 周） */
export function isoWeekNo(date: string): number {
  const thursday = addDays(weekStartOf(date), 3);
  const jan1 = `${thursday.slice(0, 4)}-01-01`;
  return Math.floor(diffDays(jan1, thursday) / 7) + 1;
}

export function isWorkday(date: string, cal: WorkCalendar): boolean {
  if (cal.holidays.includes(date)) return false;
  if (cal.extraWorkdays.includes(date)) return true;
  const w = weekdayOf(date);
  return w !== 0 && w !== 6;
}

export function prevWorkday(date: string, cal: WorkCalendar): string {
  let d = addDays(date, -1);
  for (let i = 0; i < 60 && !isWorkday(d, cal); i++) d = addDays(d, -1);
  return d;
}

export function nextWorkday(date: string, cal: WorkCalendar): string {
  let d = addDays(date, 1);
  for (let i = 0; i < 60 && !isWorkday(d, cal); i++) d = addDays(d, 1);
  return d;
}

/** (from, to] 区间内的工作日数；skip 中的日期（如请假日）不计 */
export function workdaysBetween(from: string, to: string, cal: WorkCalendar, skip: Set<string> = new Set()): number {
  if (to <= from) return 0;
  let n = 0;
  for (let d = addDays(from, 1); d <= to; d = addDays(d, 1)) {
    if (isWorkday(d, cal) && !skip.has(d)) n++;
  }
  return n;
}

export function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

// ---------- 中文显示 ----------

/** 10月3日 */
export function fmtMonthDay(date: string): string {
  const [, m, d] = date.split('-').map(Number);
  return `${m}月${d}日`;
}

/** 10月3日 周五 */
export function fmtDateWeek(date: string): string {
  return `${fmtMonthDay(date)} ${WEEKDAY_CN[weekdayOf(date)]}`;
}

/** 今天 / 昨天 / 明天 / 10月3日 */
export function fmtRelativeDay(date: string, today: string): string {
  const n = diffDays(today, date);
  if (n === 0) return '今天';
  if (n === -1) return '昨天';
  if (n === 1) return '明天';
  return fmtMonthDay(date);
}

/** 列表右侧的时间：今天显示 HH:mm，昨天显示“昨天”，更早显示日期 */
export function fmtListTime(iso: string, today: string): string {
  const d = new Date(iso);
  const date = cnDate(d);
  if (date === today) return cnTime(d);
  if (diffDays(date, today) === 1) return '昨天';
  return fmtMonthDay(date);
}

/** 拼接中文与英文 / 数字时补一个空格：“iBMS 4.0” + “交付日期” → “iBMS 4.0 交付日期” */
export function joinCN(a: string, b: string): string {
  if (!a) return b;
  if (!b) return a;
  const needSpace = /[A-Za-z0-9]$/.test(a) || /^[A-Za-z0-9]/.test(b);
  return needSpace && !/\s$/.test(a) && !/^\s/.test(b) ? `${a} ${b}` : `${a}${b}`;
}
